import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { agendaService, AgendaEvento, AgendaCombo } from '../services/agendaService';
import { tareaService } from '../services/tareaService';
import { getTiposElemento } from '../services/tipoElementoService';
import { useClienteSucursalFilter } from '../hooks/useClienteSucursalFilter';

const INSTALACIONES_FIJAS_ID = 1;
const ESTADO_ASIGNADA = 2;

const monthNames = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];
const weekDays = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function toLocalInputValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const CalendarPage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<AgendaEvento[]>([]);
  const [pendientes, setPendientes] = useState<AgendaEvento[]>([]);
  const [tiposTarea, setTiposTarea] = useState<{ id: number; nombre: string }[]>([]);
  const [tipoFiltro, setTipoFiltro] = useState<number | ''>('');
  const [_loadingEvents, setLoadingEvents] = useState(false);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendientesError, setPendientesError] = useState<string | null>(null);

  const [sidebarTab, setSidebarTab] = useState<'pendientes' | 'asignados'>('pendientes');
  const [assignItem, setAssignItem] = useState<AgendaEvento | null>(null);
  const [usuarios, setUsuarios] = useState<AgendaCombo[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [assignStart, setAssignStart] = useState(toLocalInputValue(new Date()));
  const [assignEnd, setAssignEnd] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [tiposProducto, setTiposProducto] = useState<{ id: number; nombre: string }[]>([]);
  const [tiposElemento, setTiposElemento] = useState<{ id: number; nombre: string }[]>([]);
  const [periodicidades, setPeriodicidades] = useState<{ id: number; nombre: string }[]>([]);
  const [createClienteId, setCreateClienteId] = useState<number | ''>('');
  const [createSucursalId, setCreateSucursalId] = useState<number | ''>('');
  const [createProductoId, setCreateProductoId] = useState<number | ''>('');
  const [createElementoId, setCreateElementoId] = useState<number | ''>('');
  const [createPeriodicidadId, setCreatePeriodicidadId] = useState<number | ''>('');
  const [createFecha, setCreateFecha] = useState(toLocalInputValue(new Date()));
  const [createDescripcion, setCreateDescripcion] = useState('');
  const { clienteOptions, sucursalOptions, loadSucursalesForCliente } = useClienteSucursalFilter();

  const range = useMemo(() => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
    return { start, end };
  }, [currentDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tipos = await tareaService.getTiposTarea();
        if (!cancelled) setTiposTarea(Array.isArray(tipos) ? tipos : []);
      } catch {
        if (!cancelled) setTiposTarea([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    setError(null);
    try {
      const ev = await agendaService.getEvents({
        start: range.start.toISOString(),
        end: range.end.toISOString(),
      });
      setEvents(Array.isArray(ev) ? ev : []);
    } catch (e: any) {
      setError(e?.message || 'Error cargando calendario');
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [range.start, range.end]);

  const loadPendientes = useCallback(async () => {
    setLoadingPendientes(true);
    setPendientesError(null);
    try {
      const pend = await agendaService.getPendientes({
        tipoTareaId: tipoFiltro === '' ? null : tipoFiltro,
      });
      setPendientes(Array.isArray(pend) ? pend : []);
    } catch (e: any) {
      setPendientesError(e?.message || 'Error cargando pendientes');
      setPendientes([]);
    } finally {
      setLoadingPendientes(false);
    }
  }, [tipoFiltro]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadPendientes();
  }, [loadPendientes]);

  const load = useCallback(async () => {
    await Promise.all([loadEvents(), loadPendientes()]);
  }, [loadEvents, loadPendientes]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, AgendaEvento[]>();
    for (const ev of events) {
      const d = startOfDay(new Date(ev.fecha));
      const key = d.toISOString().slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    return map;
  }, [events]);

  const monthDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const adjustedFirst = first.getDay() === 0 ? 6 : first.getDay() - 1;
    const days: { date: Date; inMonth: boolean; isToday: boolean }[] = [];
    const prevLast = new Date(year, month, 0).getDate();
    for (let i = adjustedFirst - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevLast - i),
        inMonth: false,
        isToday: false,
      });
    }
    const today = startOfDay(new Date());
    for (let d = 1; d <= last.getDate(); d++) {
      const date = new Date(year, month, d);
      days.push({
        date,
        inMonth: true,
        isToday: startOfDay(date).getTime() === today.getTime(),
      });
    }
    while (days.length % 7 !== 0) {
      const n = days.length - (adjustedFirst + last.getDate()) + 1;
      days.push({
        date: new Date(year, month + 1, n),
        inMonth: false,
        isToday: false,
      });
    }
    return days;
  }, [currentDate]);

  const openAssign = async (item: AgendaEvento) => {
    if (!item.tareaId) {
      setAssignError('El ítem no tiene tarea asociada.');
      return;
    }
    setAssignItem(item);
    setAssignError(null);
    setSelectedUserIds([]);
    const start = new Date();
    start.setMinutes(0, 0, 0);
    start.setHours(9);
    setAssignStart(toLocalInputValue(start));
    const end = new Date(start);
    end.setHours(11);
    setAssignEnd(toLocalInputValue(end));
    try {
      const list = await agendaService.getUsuariosParaTarea(item.tareaId);
      setUsuarios(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setUsuarios([]);
      setAssignError(e?.message || 'No se pudieron cargar usuarios');
    }
  };

  const toggleUser = (id: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const submitAssign = async () => {
    if (!assignItem || selectedUserIds.length === 0) {
      setAssignError('Seleccioná al menos un usuario.');
      return;
    }
    setAssigning(true);
    setAssignError(null);
    try {
      await agendaService.asignar({
        tareaId: assignItem.tareaId,
        relevamientoId: assignItem.relevamientoId ?? null,
        start: new Date(assignStart).toISOString(),
        end: assignEnd ? new Date(assignEnd).toISOString() : null,
        usuarios: selectedUserIds,
      });
      setAssignItem(null);
      setSidebarTab('asignados');
      await load();
    } catch (e: any) {
      setAssignError(e?.message || 'Error al asignar');
    } finally {
      setAssigning(false);
    }
  };

  const asignados = useMemo(() => {
    return events.filter((ev) => {
      if (ev.estadoDeTareaId !== ESTADO_ASIGNADA) return false;
      if (tipoFiltro !== '' && ev.tipoDeTareaId !== tipoFiltro) return false;
      return true;
    });
  }, [events, tipoFiltro]);

  const openCreate = async () => {
    setShowCreate(true);
    setCreateError(null);
    setCreateClienteId('');
    setCreateSucursalId('');
    setCreateProductoId('');
    setCreateElementoId('');
    setCreatePeriodicidadId('');
    setCreateFecha(toLocalInputValue(new Date()));
    setCreateDescripcion('');
    try {
      const [prods, elems, per] = await Promise.all([
        tareaService.getTiposProducto(),
        getTiposElemento(),
        tareaService.getPeriodicidades(),
      ]);
      setTiposProducto(Array.isArray(prods) ? prods : []);
      setTiposElemento(
        Array.isArray(elems)
          ? elems.filter((t) => t.activo !== false).map((t) => ({ id: t.id, nombre: t.nombre }))
          : [],
      );
      setPeriodicidades(Array.isArray(per) ? per : []);
    } catch (e: any) {
      setCreateError(e?.message || 'No se pudieron cargar catálogos');
    }
  };

  const submitCreate = async () => {
    if (!createSucursalId || !createProductoId) {
      setCreateError('Completá cliente, sucursal y tipo de producto.');
      return;
    }
    if (createProductoId === INSTALACIONES_FIJAS_ID && !createElementoId) {
      setCreateError('Para instalaciones fijas elegí el tipo de elemento.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await agendaService.crearRelevamiento({
        sucursalId: Number(createSucursalId),
        tipoDeProductoId: Number(createProductoId),
        tipoDeElementoId: createElementoId === '' ? null : Number(createElementoId),
        periodicidadId: createPeriodicidadId === '' ? null : Number(createPeriodicidadId),
        fecha: new Date(createFecha).toISOString(),
        descripcion: createDescripcion || undefined,
      });
      setShowCreate(false);
      setTipoFiltro(2);
      await load();
    } catch (e: any) {
      setCreateError(e?.message || 'Error al crear relevamiento');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agenda</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Asigná solicitudes y relevamientos pendientes a técnicos
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
        >
          <PlusIcon className="w-4 h-4" />
          Nuevo relevamiento
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-96 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm flex flex-col max-h-[calc(100vh-8rem)]">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Agenda</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Filtrá por tipo y asigná a técnicos
            </p>
            <div className="mt-3 flex rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
              <button
                type="button"
                onClick={() => setSidebarTab('pendientes')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  sidebarTab === 'pendientes'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Pendientes ({pendientes.length})
              </button>
              <button
                type="button"
                onClick={() => setSidebarTab('asignados')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  sidebarTab === 'asignados'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Asignados ({asignados.length})
              </button>
            </div>
          </div>

          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Tipo
            </label>
            <select
              value={tipoFiltro}
              onChange={(e) => setTipoFiltro(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
            >
              <option value="">Todos</option>
              {tiposTarea.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="p-4 flex-1 overflow-y-auto">
            {sidebarTab === 'pendientes' ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardDocumentListIcon className="w-4 h-4 text-orange-500" />
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Pendientes de asignar
                  </h3>
                </div>
                {loadingPendientes && <p className="text-sm text-gray-500">Cargando…</p>}
                {pendientesError && (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-2">{pendientesError}</p>
                )}
                {!loadingPendientes && !pendientesError && pendientes.length === 0 && (
                  <p className="text-sm text-gray-500">
                    No hay ítems pendientes en el rango (±2 meses).
                  </p>
                )}
                <ul className="space-y-2">
                  {pendientes.map((p) => (
                    <li
                      key={`${p.tipoDeTareaId}-${p.tareaId}-${p.relevamientoId ?? 0}`}
                      className="rounded-lg border border-gray-200 dark:border-gray-600 p-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {p.title || p.nombre}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {p.tipoDeTarea} · {new Date(p.fecha).toLocaleDateString('es-AR')}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => openAssign(p)}
                          className="shrink-0 text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                        >
                          Asignar
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardDocumentListIcon className="w-4 h-4 text-blue-500" />
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Asignados del mes
                  </h3>
                </div>
                {_loadingEvents && <p className="text-sm text-gray-500">Cargando…</p>}
                {!_loadingEvents && asignados.length === 0 && (
                  <p className="text-sm text-gray-500">
                    No hay ítems asignados en el mes del calendario.
                  </p>
                )}
                <ul className="space-y-2">
                  {asignados.map((p) => (
                    <li
                      key={`asig-${p.tipoDeTareaId}-${p.tareaId}-${p.relevamientoId ?? 0}-${p.start}`}
                      className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {p.title || p.nombre}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {p.tipoDeTarea} · {new Date(p.fecha).toLocaleDateString('es-AR')}
                          {p.usuario ? ` · ${p.usuario}` : ''}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </aside>

        <section className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))
                }
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-semibold capitalize text-gray-900 dark:text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                type="button"
                onClick={() =>
                  setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))
                }
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setCurrentDate(new Date())}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            >
              Hoy
            </button>
          </div>

          {error && <div className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</div>}

          <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
            {weekDays.map((d) => (
              <div
                key={d}
                className="bg-gray-50 dark:bg-gray-900 px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center"
              >
                {d}
              </div>
            ))}
            {monthDays.map(({ date, inMonth, isToday }) => {
              const key = startOfDay(date).toISOString().slice(0, 10);
              const dayEvents = eventsByDay.get(key) || [];
              return (
                <div
                  key={key + String(inMonth)}
                  className={`min-h-[7rem] bg-white dark:bg-gray-800 p-1.5 ${
                    !inMonth ? 'opacity-40' : ''
                  } ${isToday ? 'ring-2 ring-inset ring-red-500' : ''}`}
                >
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <div
                        key={`${ev.tareaId}-${ev.relevamientoId ?? 0}-${ev.start}`}
                        title={ev.title}
                        className="text-[10px] leading-tight truncate rounded px-1 py-0.5 text-white"
                        style={{ backgroundColor: ev.color || '#3b82f6' }}
                      >
                        {ev.title || ev.nombre}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-gray-500">+{dayEvents.length - 3} más</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {assignItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 shadow-xl p-5">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Asignar</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {assignItem.title || assignItem.nombre}
              </p>

              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Inicio
              </label>
              <input
                type="datetime-local"
                value={assignStart}
                onChange={(e) => setAssignStart(e.target.value)}
                className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              />

              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Fin (opcional)
              </label>
              <input
                type="datetime-local"
                value={assignEnd}
                onChange={(e) => setAssignEnd(e.target.value)}
                className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              />

              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Usuarios
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg mb-3">
                {usuarios.length === 0 ? (
                  <p className="p-3 text-sm text-gray-500">Sin usuarios disponibles</p>
                ) : (
                  usuarios.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(u.id)}
                        onChange={() => toggleUser(u.id)}
                      />
                      <span className="text-gray-900 dark:text-white">{u.nombre}</span>
                    </label>
                  ))
                )}
              </div>

              {assignError && (
                <p className="text-sm text-red-600 dark:text-red-400 mb-3">{assignError}</p>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAssignItem(null)}
                  className="px-3 py-2 text-sm rounded border border-gray-300 dark:border-gray-600"
                  disabled={assigning}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={submitAssign}
                  disabled={assigning}
                  className="px-3 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {assigning ? 'Asignando…' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl p-5 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Nuevo relevamiento
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Se crea en estado Pendiente y queda listo para asignar
              </p>

              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Cliente
              </label>
              <select
                value={createClienteId}
                onChange={async (e) => {
                  const v = e.target.value === '' ? '' : Number(e.target.value);
                  setCreateClienteId(v);
                  setCreateSucursalId('');
                  await loadSucursalesForCliente(v === '' ? undefined : v);
                }}
                className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              >
                <option value="">Seleccionar…</option>
                {clienteOptions.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>

              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Sucursal
              </label>
              <select
                value={createSucursalId}
                onChange={(e) =>
                  setCreateSucursalId(e.target.value === '' ? '' : Number(e.target.value))
                }
                disabled={!createClienteId}
                className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm disabled:opacity-60"
              >
                <option value="">Seleccionar…</option>
                {sucursalOptions.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>

              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Tipo de producto
              </label>
              <select
                value={createProductoId}
                onChange={(e) => {
                  const v = e.target.value === '' ? '' : Number(e.target.value);
                  setCreateProductoId(v);
                  if (v !== INSTALACIONES_FIJAS_ID) setCreateElementoId('');
                }}
                className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              >
                <option value="">Seleccionar…</option>
                {tiposProducto.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              </select>

              {createProductoId === INSTALACIONES_FIJAS_ID && (
                <>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Tipo de elemento
                  </label>
                  <select
                    value={createElementoId}
                    onChange={(e) =>
                      setCreateElementoId(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                  >
                    <option value="">Seleccionar…</option>
                    {tiposElemento.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nombre}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Periodicidad (próximo ciclo)
              </label>
              <select
                value={createPeriodicidadId}
                onChange={(e) =>
                  setCreatePeriodicidadId(e.target.value === '' ? '' : Number(e.target.value))
                }
                className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              >
                <option value="">Sin periodicidad</option>
                {periodicidades.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>

              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Fecha
              </label>
              <input
                type="datetime-local"
                value={createFecha}
                onChange={(e) => setCreateFecha(e.target.value)}
                className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              />

              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Descripción (opcional)
              </label>
              <textarea
                value={createDescripcion}
                onChange={(e) => setCreateDescripcion(e.target.value)}
                rows={2}
                className="w-full mb-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              />

              {createError && (
                <p className="text-sm text-red-600 dark:text-red-400 mb-3">{createError}</p>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-3 py-2 text-sm rounded border border-gray-300 dark:border-gray-600"
                  disabled={creating}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={submitCreate}
                  disabled={creating}
                  className="px-3 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {creating ? 'Creando…' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
